import {
  CHINA_MAP_LABEL_FONT_SIZE,
  CHINA_MAP_LABEL_GLYPH_HALF_SIZE,
  getChinaMapLabelLineOffsets,
  getChinaMapLabelOffsetUnit,
  type ChinaMapPlace,
} from "@-/dev.improve.haniverse.data.china-map";
import {
  getChinaMapPlaceRings,
  isChinaMapBboxInsideRings,
  measureChinaMapRingsRoom,
  type ChinaMapBbox,
} from "#china-map-geometry";
import type { ChinaMapLabel } from "#china-map-places";

type Offsets = readonly (readonly [number, number])[];

/** The visible part of a region is sampled at about this many spots. */
const ROOM_SAMPLES = 256;
/** Spots the samples cannot settle either way are measured exactly, up to this many per name. */
const MAX_EXACT_SPOTS = 32;
/** Each smaller size a name is tried at when its region's visible part cannot hold it. */
const SHRINK_STEP = 0.85;
/** A layout's stored size is found by sampling, so it is still tried a little beyond that size. */
const EM_TOLERANCE = 1.05;
/** A full-size name's room for zooming out is checked in steps of this much, up to `MAX_GROWTH`. */
const GROWTH_STEP = 1.1;
const MAX_GROWTH = 4;

/** Whether every character's square is within the visible area. */
function isChinaMapLabelInViewport(
  { fontSize, layout }: ChinaMapLabel,
  scale: number,
  [minX, minY, maxX, maxY]: ChinaMapBbox,
): boolean {
  const em = fontSize / scale;
  const half = em * CHINA_MAP_LABEL_GLYPH_HALF_SIZE;
  const unit = getChinaMapLabelOffsetUnit(layout, em);
  return layout.offsets.every(([offsetX, offsetY]) => {
    const x = layout.anchor[0] + offsetX * unit;
    const y = layout.anchor[1] + offsetY * unit;
    return x - half >= minX && x + half <= maxX && y - half >= minY && y + half <= maxY;
  });
}

/**
 * The plain layouts (across, then down), then the region's other unscattered ones, each with the
 * largest size it holds the name at anywhere in the region (unknown for a plain layout the build
 * did not list). Scattered characters are pinned to their spots, so they cannot move.
 */
function getMovableLayouts(place: ChinaMapPlace, characterCount: number) {
  const line = getChinaMapLabelLineOffsets(characterCount);
  const layouts = new Map<string, { em: number; offsets: Offsets }>();
  for (const offsets of [line, line.map(([x, y]): [number, number] => [y, x])]) {
    layouts.set(JSON.stringify(offsets), { em: Number.POSITIVE_INFINITY, offsets });
  }
  for (const { em, isScattered, offsets } of place.labels) {
    if (isScattered !== true) {
      layouts.set(JSON.stringify(offsets), { em, offsets });
    }
  }
  return [...layouts.values()];
}

/**
 * Moves a name that the edge of the visible area cuts off into the part of its region that is
 * visible, as close as possible to where it was. It keeps its size if it can, and otherwise
 * shrinks, but no smaller than `minFontSize`. The name stays as it was if the visible part cannot
 * hold it, or if no part of its region is visible.
 */
function placeChinaMapLabelInViewport(
  label: ChinaMapLabel,
  {
    minFontSize,
    scale,
    visibleBbox,
  }: { minFontSize: number; scale: number; visibleBbox: ChinaMapBbox },
): ChinaMapLabel {
  const { fontSize, layout, view } = label;
  const characterCount = layout.offsets.length;
  if (
    fontSize === 0 ||
    characterCount === 0 ||
    label.leader !== undefined ||
    isChinaMapLabelInViewport(label, scale, visibleBbox)
  ) {
    return label;
  }
  const [viewMinX, viewMinY, viewMaxX, viewMaxY] = visibleBbox;
  // Each character must be on the region's land and in view, so within both rectangles.
  const minX = Math.max(view.place.bbox[0], viewMinX);
  const minY = Math.max(view.place.bbox[1], viewMinY);
  const maxX = Math.min(view.place.bbox[2], viewMaxX);
  const maxY = Math.min(view.place.bbox[3], viewMaxY);
  if (!(maxX > minX && maxY > minY)) {
    return label;
  }
  const rings = getChinaMapPlaceRings(view.place);

  // Square cells, but never so many along a sliver's length.
  const cell = Math.max(
    Math.sqrt(((maxX - minX) * (maxY - minY)) / ROOM_SAMPLES),
    Math.max(maxX - minX, maxY - minY) / Math.sqrt(ROOM_SAMPLES) / 2,
    1 / scale,
  );
  const columns = Math.ceil((maxX - minX) / cell) + 1;
  const rows = Math.ceil((maxY - minY) / cell) + 1;
  // Any sample with this much room settles that the largest size fits, so no farther edge matters.
  const roomLimit = (fontSize / scale) * CHINA_MAP_LABEL_GLYPH_HALF_SIZE + cell / 2;
  // Sampled only where a candidate looks, and shared by every size and layout tried. The room of a
  // spot (see `measureChinaMapRingsRoom`) is also kept clear of the edge of the view.
  const sampled = new Float64Array(columns * rows).fill(Number.NaN);
  const sampleRoom = (column: number, row: number) => {
    const index = row * columns + column;
    let room = sampled[index] ?? Number.NaN;
    if (Number.isNaN(room)) {
      const x = minX + column * cell;
      const y = minY + row * cell;
      const viewRoom = Math.min(x - viewMinX, viewMaxX - x, y - viewMinY, viewMaxY - y);
      room =
        viewRoom <= 0
          ? viewRoom
          : Math.min(
              viewRoom,
              measureChinaMapRingsRoom(rings, x, y, Math.min(viewRoom, roomLimit)),
            );
      sampled[index] = room;
    }
    return room;
  };
  const [targetX, targetY] = layout.anchor;
  const distances = Float64Array.from(
    { length: columns * rows },
    (_, index) =>
      (minX + (index % columns) * cell - targetX) ** 2 +
      (minY + Math.floor(index / columns) * cell - targetY) ** 2,
  );
  const spots = Array.from({ length: columns * rows }, (_, index) => index).sort(
    (a, b) => (distances[a] ?? 0) - (distances[b] ?? 0),
  );

  /** Whether every character's square is inside the region (and in view), measured exactly. */
  const fits = (
    offsets: Offsets,
    em: number,
    x: number,
    y: number,
    { isInView = true }: { isInView?: boolean } = {},
  ) => {
    const half = em * CHINA_MAP_LABEL_GLYPH_HALF_SIZE;
    return offsets.every(([offsetX, offsetY]) => {
      const box: ChinaMapBbox = [
        x + offsetX * em - half,
        y + offsetY * em - half,
        x + offsetX * em + half,
        y + offsetY * em + half,
      ];
      return (
        (!isInView ||
          (box[0] >= viewMinX && box[1] >= viewMinY && box[2] <= viewMaxX && box[3] <= viewMaxY)) &&
        isChinaMapBboxInsideRings(rings, box)
      );
    });
  };
  let exactSpots = 0;
  /** The sampled spot nearest the old anchor that holds the name, measured exactly if need be. */
  const findSpot = (offsets: Offsets, em: number): [number, number] | null => {
    const half = em * CHINA_MAP_LABEL_GLYPH_HALF_SIZE;
    for (const spot of spots) {
      const x = minX + (spot % columns) * cell;
      const y = minY + Math.floor(spot / columns) * cell;
      // Room changes no faster than the distance moved, so the nearest sample, at most half a cell
      // away on each axis, can settle whether a character fits.
      let isPossible = true;
      let isCertain = true;
      for (const [offsetX, offsetY] of offsets) {
        const characterX = x + offsetX * em;
        const characterY = y + offsetY * em;
        const room =
          characterX < minX || characterX > maxX || characterY < minY || characterY > maxY
            ? Number.NEGATIVE_INFINITY
            : sampleRoom(
                Math.round((characterX - minX) / cell),
                Math.round((characterY - minY) / cell),
              );
        if (room < half - cell / 2) {
          isPossible = false;
          break;
        }
        isCertain &&= room >= half + cell / 2;
      }
      if (isPossible && isCertain) {
        return [x, y];
      }
      if (isPossible && exactSpots < MAX_EXACT_SPOTS) {
        exactSpots += 1;
        if (fits(offsets, em, x, y)) {
          return [x, y];
        }
      }
    }
    return null;
  };
  /** Slides the name on from a spot that holds it toward its old anchor, in ever smaller steps. */
  const slideToTarget = (offsets: Offsets, em: number, [startX, startY]: [number, number]) => {
    let x = startX;
    let y = startY;
    for (let step = cell / 2; step > cell / 32; step /= 2) {
      for (let moves = 0; moves < 4; moves += 1) {
        const dx = Math.sign(targetX - x) * Math.min(step, Math.abs(targetX - x));
        const dy = Math.sign(targetY - y) * Math.min(step, Math.abs(targetY - y));
        if (dx !== 0 && dy !== 0 && fits(offsets, em, x + dx, y + dy)) {
          x += dx;
          y += dy;
        } else if (dx !== 0 && fits(offsets, em, x + dx, y)) {
          x += dx;
        } else if (dy !== 0 && fits(offsets, em, x, y + dy)) {
          y += dy;
        } else {
          break;
        }
      }
    }
    return [x, y] as const;
  };

  const sizes = [fontSize];
  const floor = Math.min(fontSize, minFontSize);
  for (let size = fontSize * SHRINK_STEP; size > floor; size *= SHRINK_STEP) {
    sizes.push(size);
  }
  if (floor < fontSize) {
    sizes.push(floor);
  }
  const layouts = getMovableLayouts(view.place, characterCount);
  for (const size of sizes) {
    const em = size / scale;
    for (const { em: largestEm, offsets } of layouts) {
      // Nowhere in the region does this layout hold the name this large, let alone in part of it.
      if (largestEm * scale * EM_TOLERANCE < size) {
        continue;
      }
      const spot = findSpot(offsets, em);
      if (spot === null) {
        continue;
      }
      const [x, y] = slideToTarget(offsets, em, spot);
      // The renderers keep a name at the full size while zooming out until its layout runs out of
      // room (`em`), as for a name in place. A shrunk name instead shrinks with the map at once.
      let roomyEm = em;
      while (
        size === CHINA_MAP_LABEL_FONT_SIZE &&
        roomyEm * GROWTH_STEP <= Math.min(largestEm, em * MAX_GROWTH) &&
        fits(offsets, roomyEm * GROWTH_STEP, x, y, { isInView: false })
      ) {
        roomyEm *= GROWTH_STEP;
      }
      return { ...label, fontSize: size, layout: { anchor: [x, y], em: roomyEm, offsets } };
    }
  }
  return label;
}

/**
 * Keeps the names of regions that are only partly on screen readable: a name that the edge of the
 * visible area (between the toolbar and the sheet) would cut off, or that lies wholly outside it,
 * moves into the part of its region that is visible. Names already in view stay where they are.
 * Like every other label, this is recomputed only when the camera settles.
 */
export function placeChinaMapViewportLabels(
  labels: readonly ChinaMapLabel[],
  options: { minFontSize: number; scale: number; visibleBbox: ChinaMapBbox },
): ChinaMapLabel[] {
  if (!Number.isFinite(options.scale) || options.scale <= 0) {
    return [...labels];
  }
  return labels.map((label) => placeChinaMapLabelInViewport(label, options));
}
